import React, { useState, useCallback, useEffect, useRef } from "react";
import { Modal, Button, Form, Spinner } from "react-bootstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import { submitFeedback } from "../../General/api";
import swal from "sweetalert";

/**
 * Maximum allowed characters for feedback message
 * @constant {number}
 */
const MAX_MESSAGE_LENGTH = 500;

/**
 * Character count threshold for warning display
 * @constant {number}
 */
const CHARACTER_WARNING_THRESHOLD = 50;

/**
 * Validation schema for feedback form
 * @constant {Yup.ObjectSchema}
 */
const FeedbackSchema = Yup.object().shape({
  rating: Yup.number()
    .nullable()
    .transform((value, originalValue) => {
      // Handle null/undefined/empty values
      if (originalValue === null || originalValue === undefined || originalValue === "") {
        return null;
      }
      // Ensure it's a valid number
      const num = Number(originalValue);
      return isNaN(num) ? null : num;
    })
    .required("Satisfaction rating is required")
    .min(1, "Please select a satisfaction rating")
    .max(5, "Invalid rating")
    .typeError("Please select a satisfaction rating"),
  subjectType: Yup.string().required("Please select a feedback subject"),
  message: Yup.string()
    .required("Message is required")
    .max(MAX_MESSAGE_LENGTH, `Message must be ${MAX_MESSAGE_LENGTH} characters or less`)
    .trim()
    .min(1, "Message cannot be empty"),
});

/**
 * Rating options for satisfaction feedback
 * Professional numeric scale (1-5) for enterprise-grade feedback collection
 * Each option includes value (1-5) and accessible label
 * @constant {Array<{value: number, label: string}>}
 */
const RATING_OPTIONS = Object.freeze([
  { value: 1, label: "Very Dissatisfied" },
  { value: 2, label: "Dissatisfied" },
  { value: 3, label: "Neutral" },
  { value: 4, label: "Satisfied" },
  { value: 5, label: "Very Satisfied" },
]);

/**
 * Feedback subject type options
 * @constant {Array<{value: string, label: string}>}
 */
const SUBJECT_OPTIONS = Object.freeze([
  { value: "Suggestion", label: "Suggestion" },
  { value: "Technical issue", label: "Technical issue" },
  { value: "Compliment", label: "Compliment" },
  { value: "Other", label: "Other" },
]);

/**
 * FeedbackModal Component
 * 
 * A modal component for collecting user feedback with satisfaction rating,
 * subject categorization, and detailed message input.
 * 
 * @param {Object} props - Component props
 * @param {boolean} props.show - Controls modal visibility
 * @param {Function} props.onHide - Callback function to close the modal
 * @returns {JSX.Element} Feedback modal component
 */
export function FeedbackModal({ show, onHide }) {
  const [loading, setLoading] = useState(false);
  const sliderWrapperRef = useRef(null);

  const formik = useFormik({
    initialValues: {
      rating: null,
      subjectType: "",
      message: "",
    },
    validationSchema: FeedbackSchema,
    validateOnChange: true,
    validateOnBlur: true,
    validateOnMount: false,
    onSubmit: async (values, { resetForm, setFieldError }) => {
      setLoading(true);
      try {
        const payload = {
          rating: values.rating,
          subjectType: values.subjectType,
          message: values.message.trim(),
          currentUrl: window.location.href,
        };

        const response = await submitFeedback(payload);
        
        // Check if response indicates success
        if (response?.data?.success !== false) {
          swal("Success", "Thank you for your feedback!", "success");
          resetForm();
          onHide();
        } else {
          // Handle API-level error messages
          const errorMessage = response?.data?.message || "Unable to submit feedback. Please try again.";
          swal("Error", errorMessage, "error");
        }
      } catch (error) {
        console.error("Feedback submission error:", error);
        
        // Extract user-friendly error message from API response
        let errorMessage = "Something went wrong. Please try again.";
        if (error?.response?.data?.message) {
          errorMessage = error.response.data.message;
        } else if (error?.message) {
          errorMessage = error.message;
        }
        
        swal("Error", errorMessage, "error");
      } finally {
        setLoading(false);
      }
    },
  });

  /**
   * Handles rating button click with proper form state management
   * @param {number} ratingValue - The selected rating value (1-5)
   */
  const handleRatingClick = useCallback(
    (ratingValue) => {
      // Ensure ratingValue is a valid number
      const numericValue = Number(ratingValue);
      if (!isNaN(numericValue) && numericValue >= 1 && numericValue <= 5) {
        // Set value and mark as touched, Formik will validate automatically
        formik.setFieldValue("rating", numericValue);
        formik.setFieldTouched("rating", true);
      }
    },
    [formik]
  );

  /**
   * Handles slider drag/change for rating selection
   * @param {React.ChangeEvent<HTMLInputElement>} event - Slider change event
   */
  const handleSliderChange = useCallback(
    (event) => {
      const value = parseInt(event.target.value, 10);
      if (value >= 1 && value <= 5) {
        formik.setFieldValue("rating", value);
        formik.setFieldTouched("rating", true);
      }
    },
    [formik]
  );

  /**
   * Handles keyboard events for rating buttons (Enter/Space)
   * @param {React.KeyboardEvent} event - Keyboard event
   * @param {number} ratingValue - The rating value to select
   */
  const handleRatingKeyDown = useCallback(
    (event, ratingValue) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        handleRatingClick(ratingValue);
      }
    },
    [handleRatingClick]
  );

  const messageLength = formik.values.message?.length || 0;
  const charactersRemaining = MAX_MESSAGE_LENGTH - messageLength;
  const showCharacterWarning = charactersRemaining < CHARACTER_WARNING_THRESHOLD;

  // Update slider progress indicator dynamically
  useEffect(() => {
    if (sliderWrapperRef.current && formik.values.rating) {
      const rating = formik.values.rating;
      // Calculate progress: 1=0%, 2=25%, 3=50%, 4=75%, 5=100%
      const progress = ((rating - 1) / 4) * 100;
      sliderWrapperRef.current.style.setProperty('--slider-progress', `${progress}%`);
    } else if (sliderWrapperRef.current) {
      // Default to 0% if no rating selected
      sliderWrapperRef.current.style.setProperty('--slider-progress', '0%');
    }
  }, [formik.values.rating]);

  return (
    <Modal show={show} onHide={onHide} centered size="md">
      <Modal.Header closeButton>
        <Modal.Title>Rate This Page</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form onSubmit={formik.handleSubmit}>
          {/* Satisfaction Rating */}
          <Form.Group className="mb-4">
            <Form.Label className="font-weight-bold">
              How satisfied are you with the features on this page?
            </Form.Label>
            <div
              className="feedback-rating-container mt-3"
              role="radiogroup"
              aria-label="Satisfaction rating"
            >
              {/* Numeric Scale 1-5 */}
              <div className="d-flex justify-content-between mb-3" style={{ maxWidth: "400px", margin: "0 auto", position: "relative" }}>
                {RATING_OPTIONS.map((option) => {
                  const isSelected = formik.values.rating === option.value;
                  return (
                    <button
                      key={option.value}
                      type="button"
                      role="radio"
                      aria-checked={isSelected}
                      aria-label={`Rate ${option.value} - ${option.label}`}
                      onClick={() => handleRatingClick(option.value)}
                      onKeyDown={(e) => handleRatingKeyDown(e, option.value)}
                      tabIndex={isSelected ? 0 : -1}
                      className={`feedback-rating-number ${isSelected ? "selected" : ""}`}
                      title={option.label}
                    >
                      {option.value}
                    </button>
                  );
                })}
              </div>
              {/* Draggable Slider */}
              <div 
                ref={sliderWrapperRef}
                className="feedback-rating-slider-wrapper" 
                style={{ maxWidth: "400px", margin: "0 auto 12px auto", padding: "0 25px" }}
              >
                <input
                  type="range"
                  min="1"
                  max="5"
                  step="1"
                  value={formik.values.rating || 1}
                  onChange={handleSliderChange}
                  className="feedback-rating-slider"
                  aria-label="Satisfaction rating slider"
                  aria-valuemin={1}
                  aria-valuemax={5}
                  aria-valuenow={formik.values.rating || 1}
                />
              </div>
              {/* Labels Below */}
              <div className="d-flex justify-content-between" style={{ maxWidth: "400px", margin: "0 auto" }}>
                <span className="feedback-rating-label text-muted small">Very Dissatisfied</span>
                <span className="feedback-rating-label text-muted small">Very Satisfied</span>
              </div>
            </div>
            {formik.touched.rating && formik.errors.rating && (
              <div className="text-danger small mt-2">{formik.errors.rating}</div>
            )}
          </Form.Group>

          {/* Feedback Subject Dropdown */}
          <Form.Group className="mb-3">
            <Form.Label htmlFor="feedback-subject" className="font-weight-bold">
              Feedback subject
            </Form.Label>
            <Form.Control
              id="feedback-subject"
              as="select"
              name="subjectType"
              aria-describedby="subject-error"
              {...formik.getFieldProps("subjectType")}
              isInvalid={formik.touched.subjectType && formik.errors.subjectType}
            >
              <option value="">Select a subject</option>
              {SUBJECT_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Form.Control>
            <Form.Control.Feedback type="invalid" id="subject-error">
              {formik.errors.subjectType}
            </Form.Control.Feedback>
          </Form.Group>

          {/* Feedback Message */}
          <Form.Group className="mb-2">
            <Form.Label htmlFor="feedback-message" className="font-weight-bold">
              What is your feedback?
            </Form.Label>
            <Form.Control
              id="feedback-message"
              as="textarea"
              rows={4}
              name="message"
              placeholder="Do not include personal or sensitive data in your feedback."
              maxLength={MAX_MESSAGE_LENGTH}
              aria-describedby="message-help message-error"
              {...formik.getFieldProps("message")}
              isInvalid={formik.touched.message && formik.errors.message}
            />
            <Form.Control.Feedback type="invalid" id="message-error">
              {formik.errors.message}
            </Form.Control.Feedback>
            <Form.Text id="message-help" className="sr-only">
              Maximum {MAX_MESSAGE_LENGTH} characters allowed
            </Form.Text>
          </Form.Group>

          {/* Character Counter */}
          <div className="d-flex justify-content-end align-items-center mb-3">
            <small
              className={`feedback-char-counter ${showCharacterWarning ? "warning" : "text-muted"}`}
              aria-live="polite"
              aria-atomic="true"
            >
              {charactersRemaining} characters remaining
            </small>
          </div>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={formik.handleSubmit}
          disabled={
            loading ||
            !formik.values.rating ||
            !formik.values.subjectType ||
            !formik.values.message ||
            formik.values.message.trim().length === 0
          }
          aria-busy={loading}
        >
          {loading ? (
            <>
              <Spinner animation="border" size="sm" className="mr-2" aria-hidden="true" />
              <span>Submitting...</span>
            </>
          ) : (
            "Submit"
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
